// TestTheAPI.jsx
// =============================================
// Test API keys with minimal token usage to validate functionality
// Makes a lightweight call to the /api/singlecall backend endpoint to check if the key works
// =============================================
import React, { useState } from 'react';
import { Loader, Check, X } from 'lucide-react';
import { authManager } from '@/managers/AuthManager';
import { generateId } from '@/managers/ConvoManager/ConvoHelpers';
import { platformCannon } from '@/managers/ChatFeedManager/PlatformCannon';

// Models chosen for minimal token usage and cost — updated March 2026
const TEST_MODELS = {
  OPENAI: 'gpt-4.1-nano',           // cheapest OpenAI model ~$0.10/M
  CLAUDE: 'claude-haiku-4-5',       // cheapest Claude model $1/M (no free tier)
  GEMINI: 'gemini-2.5-flash-lite',  // cheapest Gemini $0.10/M, has free tier
  MISTRAL: 'mistral-small-latest',  // small model alias, always current
  OPENROUTER: 'qwen/qwen3.5-9b',   // cheap $0.10/M, stable — llama free endpoints removed
  GROK: 'grok-3-mini',             // cheapest xAI model
};

const TEST_PROMPT = `Return a JSON object with a single key, "status", and set its value to the string "API_KEY_VALID".`;

const RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    status: { type: 'string' },
  },
  required: ['status'],
};

/* ========================
 * testApiKey - Validates a provider API key by calling the backend's /singlecall endpoint.
 * ======================== */
export async function testApiKey(providerId, apiKey) {
  if (!apiKey || !providerId) {
    return { valid: false, message: 'API key and provider ID are required.' };
  }

  const currentUser = authManager.getCurrentUser();
  if (!currentUser?.uid) {
    return { valid: false, message: 'User not authenticated. Please sign in.' };
  }

  let idToken;
  try {
    idToken = await currentUser.getIdToken();
  } catch (tokenError) {
    return { valid: false, message: 'Failed to retrieve authentication token. Please try signing in again.' };
  }

  const model = TEST_MODELS[providerId];
  if (!model) {
    return { valid: false, message: `Configuration error: No test model specified for ${providerId}.` };
  }
  
  const singleCallPayload = {
    platform: providerId.toUpperCase(), 
    apiKey: apiKey,
    prompt: TEST_PROMPT, 
    model: model,
    responseSchema: RESPONSE_SCHEMA
  };

  const requestId = `test_${providerId}_${generateId('')}`;
  const fullApiUrl = `${platformCannon.cloudRunUrl}/singlecall`;

  try {
    const response = await fetch(fullApiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Request-Id': requestId,
            'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(singleCallPayload),
    });
    
    const result = await response.json();

    if (response.ok) {
        if (result.success && result.payload?.status === 'API_KEY_VALID') {
            return { valid: true, message: 'API key is valid.' };
        } else {
            return { valid: true, message: 'API key accepted, but response format was unexpected.' };
        }
    } else {
        const serverErrorMessage = result.message || `API test for ${providerId} failed.`;

        if (String(serverErrorMessage).toLowerCase().includes('api key') || response.status === 401 || response.status === 403) {
            return { valid: false, message: 'Invalid API key or insufficient permissions.' };
        } else if (String(serverErrorMessage).toLowerCase().includes('quota') || String(serverErrorMessage).toLowerCase().includes('credit')) {
            return { valid: false, message: `Key may be valid, but there's an issue with API credits/quota for ${providerId}.` };
        } else if (String(serverErrorMessage).toLowerCase().includes('rate') || String(serverErrorMessage).toLowerCase().includes('limit')) {
            return { valid: false, message: `Rate limit exceeded for ${providerId}. Please try again later.` };
        } else if (response.status === 500) {
            return { valid: false, message: `The server encountered an internal error while testing the ${providerId} key.`};
        }
        return { valid: false, message: serverErrorMessage };
    }
  } catch (criticalError) { 
    console.error("Critical fetch error in testApiKey:", criticalError);
    return { valid: false, message: `A critical network error occurred while testing the ${providerId} key. Is the server running?` };
  }
}

/**
 * testCustomEndpoint — validates a user-configured OpenAI-compatible endpoint.
 * Calls /singlecall with platform=CUSTOM, passing baseUrl + modelId so the server
 * can probe the endpoint without ever touching the client's network.
 */
export async function testCustomEndpoint(baseUrl, modelId, apiKey) {
  if (!baseUrl || !modelId || !apiKey) {
    return { valid: false, message: 'Base URL, Model ID, and API Key are all required.' };
  }
  const currentUser = authManager.getCurrentUser();
  if (!currentUser?.uid) {
    return { valid: false, message: 'User not authenticated. Please sign in.' };
  }
  let idToken;
  try {
    idToken = await currentUser.getIdToken();
  } catch {
    return { valid: false, message: 'Failed to retrieve authentication token.' };
  }

  const requestId = `test_CUSTOM_${generateId('')}`;
  const fullApiUrl = `${platformCannon.cloudRunUrl}/singlecall`;

  try {
    const response = await fetch(fullApiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-Id': requestId,
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        platform: 'CUSTOM',
        apiKey,
        baseUrl,
        model: modelId,
        prompt: TEST_PROMPT,
      }),
    });

    const result = await response.json();
    if (response.ok && result.success) {
      return { valid: true, message: 'Endpoint reachable and key accepted.' };
    }
    return { valid: false, message: result.message || `Endpoint test failed (${response.status}).` };
  } catch (err) {
    console.error('Critical fetch error in testCustomEndpoint:', err);
    return { valid: false, message: `Network error while testing endpoint. Is the URL reachable?` };
  }
}

/**
 * discoverCustomEndpointModels — server-side proxy to list models on a custom endpoint.
 * Returns { success, models: [{ id, loaded }] } or { success: false, message }.
 */
export async function discoverCustomEndpointModels(baseUrl, apiKey) {
  if (!baseUrl || !apiKey) {
    return { success: false, message: 'Base URL and API key are required.' };
  }
  const currentUser = authManager.getCurrentUser();
  if (!currentUser?.uid) return { success: false, message: 'Not authenticated.' };
  let idToken;
  try { idToken = await currentUser.getIdToken(); } catch { return { success: false, message: 'Auth error.' }; }

  const requestId = `discover_${generateId('')}`;
  const fullApiUrl = `${platformCannon.cloudRunUrl}/listmodels`;

  try {
    const response = await fetch(fullApiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-Id': requestId,
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ baseUrl, apiKey }),
    });
    const result = await response.json();
    if (response.ok && result.success) return { success: true, models: result.models };
    return { success: false, message: result.message || 'Discovery failed.' };
  } catch (err) {
    console.error('Error in discoverCustomEndpointModels:', err);
    return { success: false, message: `Network error: ${err.message}` };
  }
}

export function TestButton({ platform, apiKey, onSuccess, onError, className = '' }) {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleTest = async () => {
    if (!apiKey || status === 'loading') return;
    
    setStatus('loading');
    try {
      const result = await testApiKey(platform, apiKey); 
      if (result.valid) {
        setStatus('success');
        onSuccess && onSuccess(result);
      } else {
        setStatus('error');
        onError && onError(result);
      }
      
      setTimeout(() => setStatus('idle'), 3000);

    } catch (error) { 
      setStatus('error');
      onError && onError({ valid: false, message: error.message || 'Client-side test error.' });
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  let buttonContent;
  let buttonClass = `flex items-center gap-1 text-xs rounded-md transition-colors ${className} `;
  
  switch (status) {
    case 'loading':
      buttonContent = (<><Loader className="w-3 h-3 animate-spin" /><span>Testing...</span></>);
      buttonClass += 'text-on-surface-dim cursor-wait';
      break;
    case 'success':
      buttonContent = (<><Check className="w-3 h-3" /><span>Valid</span></>);
      buttonClass += 'text-success';
      break;
    case 'error':
      buttonContent = (<><X className="w-3 h-3" /><span>Invalid</span></>);
      buttonClass += 'text-error';
      break;
    default: // idle
      buttonContent = 'Test Key';
      buttonClass += 'text-primary hover:text-primary-light disabled:opacity-50 disabled:cursor-not-allowed';
      break;
  }

  return (
    <button 
      onClick={handleTest}
      disabled={status === 'loading' || !apiKey}
      className={buttonClass}
    >
      {buttonContent}
    </button>
  );
}

export default TestButton;